import { useEffect } from "react";

/**
 * Per-route SEO: updates <title>, meta description, meta keywords, robots, canonical.
 * Every route should explicitly set robots ("index, follow" or "noindex, follow")
 * so SPA navigation never leaves stale crawler directives on the document.
 */
export default function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogImageMeta,
  robots = "index, follow",
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (nameOrProp, content, isProperty = false) => {
      if (content == null) return;
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${nameOrProp}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, nameOrProp);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("robots", robots);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    setMeta("og:title", ogTitle || title, true);
    setMeta("og:description", ogDescription || description, true);
    if (canonical) setMeta("og:url", canonical, true);
    if (ogImage) {
      setMeta("og:image", ogImage, true);
      setMeta("og:image:secure_url", ogImage, true);
      setMeta("twitter:image", ogImage);
      if (ogImageMeta) {
        setMeta("og:image:alt", ogImageMeta.alt, true);
        setMeta("og:image:width", ogImageMeta.width, true);
        setMeta("og:image:height", ogImageMeta.height, true);
        setMeta("og:image:type", ogImageMeta.type, true);
        setMeta("twitter:image:alt", ogImageMeta.alt);
      }
    }
    // og:image is managed in index.html and should stay consistent across routes
    setMeta("twitter:title", ogTitle || title);
    setMeta("twitter:description", ogDescription || description);
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogImageMeta, robots]);
}
